import { useState, useRef, useEffect, useCallback } from "react";
import { addLayerToggle } from "@/lib/mapLayers";
import { useNavigate } from "react-router-dom";
import { Shield, LogOut, MapPin, Clock, ClipboardList, AlertTriangle, Eye, ChevronRight, Mic, Square, Play, Pause, Map as MapIcon, Radio, CheckCircle2, Circle, Timer, ScanFace, Crosshair, Trash2, Send, Bot, ArrowLeft, Navigation, Key, Copy, Lock, Loader2, User, Camera, Image, X } from "lucide-react";
import ProfileSection from "@/components/app/ProfileSection";
import ThemeToggle from "@/components/ThemeToggle";
import secureflexLogo from "@/assets/secureflex-logo-new.png";
import DashboardBreadcrumb from "@/components/app/DashboardBreadcrumb";
import { useMotion } from "@/lib/safeMotion";
import { toast } from "sonner";
import CheckInModal from "@/components/app/CheckInModal";
import { getOfficers, getOfficerPassword, onDataChange } from "@/lib/sharedStore";
import { isOfficerLive, lastSeenAgo } from "@/lib/officerStatus";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { useFaceVerification } from "@/hooks/useFaceVerification";
import { useNotifications } from "@/hooks/useNotifications";
import ShiftClockSection from "@/components/app/ShiftClockSection";
import MapAddressSearch from "@/components/app/MapAddressSearch";
import FaceIDScanner from "@/components/app/FaceIDScanner";

const tabs = [
  { id: "shift", label: "Shift", icon: Timer },
  { id: "patrol", label: "Patrol", icon: Navigation },
  { id: "map", label: "Map", icon: MapIcon },
  { id: "schedule", label: "Schedule", icon: Clock },
  { id: "log", label: "My Log", icon: ClipboardList },
  { id: "password", label: "Password", icon: Key },
  { id: "report", label: "Report", icon: AlertTriangle },
  { id: "profile", label: "Profile", icon: User },
];

type CheckpointItem = {
  id: string;
  name: string;
  zone: string;
  due: string;
  status: "done" | "due" | "pending";
  checkedAt: string | null;
  method: string | null;
  variance: string | null;
  scheduleId: string | null;
  lat: number | null;
  lng: number | null;
  radius: number | null;
};

const OfficerDashboard = () => {
  const { motion, AnimatePresence } = useMotion();
  const navigate = useNavigate();
  const { signOut, profile, user } = useAuth();
  const [activeTab, setActiveTab] = useState("shift");
  const [checkpoints, setCheckpoints] = useState<CheckpointItem[]>([]);
  const [checkInTarget, setCheckInTarget] = useState<CheckpointItem | null>(null);
  const [isClockedIn, setIsClockedIn] = useState(false);

  // Load real officer data from DB
  const [officerRecord, setOfficerRecord] = useState<{ id: string; name: string; site_id: string | null; face_photo_url: string | null; psira: string | null } | null>(null);
  const [siteName, setSiteName] = useState("");

  // Check if officer is currently clocked in (on mount and when officer changes)
  useEffect(() => {
    if (!officerRecord?.id) return;
    const checkShift = async () => {
      const { data } = await supabase
        .from("shift_clock")
        .select("id")
        .eq("officer_id", officerRecord.id)
        .eq("status", "clocked_in")
        .limit(1);
      setIsClockedIn(!!(data && data.length > 0));
    };
    checkShift();
  }, [officerRecord?.id]);

  // Guard check-in attempts — must be clocked in first
  const handleCheckInAttempt = (cp: CheckpointItem) => {
    if (!isClockedIn) {
      toast.error("You must clock in before checking in at a checkpoint.", {
        description: "Redirecting you to the Shift tab…",
        duration: 4000,
      });
      setTimeout(() => setActiveTab("shift"), 1500);
      return;
    }
    setCheckInTarget(cp);
  };
  
  useEffect(() => {
    if (!user?.id) return;
    const fetchOfficer = async () => {
      const { data: officer } = await supabase
        .from("officers")
        .select("id, name, site_id, face_photo_url, psira")
        .eq("user_id", user.id)
        .maybeSingle();
      if (officer) {
        setOfficerRecord(officer);
        // Set status to live on dashboard mount
        await supabase.from("officers").update({ status: "live" }).eq("id", officer.id);
        if (officer.site_id) {
          const { data: site } = await supabase.from("sites").select("name").eq("id", officer.site_id).maybeSingle();
          if (site) setSiteName(site.name);
        }
      }
    };
    fetchOfficer();

    // No cleanup here — status is set to offline explicitly on sign-out button click
    // (cleanup runs after signOut clears auth, so RLS blocks the update)
  }, [user?.id]);

  // Fetch real schedule & check-ins for this officer
  useEffect(() => {
    if (!officerRecord?.id) return;
    const fetchSchedule = async () => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      const { data: schedules } = await supabase
        .from("schedules")
        .select("id, due_at, status, checkpoint_id, site_id")
        .eq("officer_id", officerRecord.id)
        .gte("due_at", today.toISOString())
        .lt("due_at", tomorrow.toISOString())
        .order("due_at", { ascending: true });

      if (!schedules || schedules.length === 0) {
        setCheckpoints([]);
        return;
      }

      const cpIds = schedules.map(s => s.checkpoint_id).filter(Boolean) as string[];
      const { data: cpData } = cpIds.length > 0
        ? await supabase.from("checkpoints").select("id, name, zone, lat, lng, radius").in("id", cpIds)
        : { data: [] };
      const cpMap = new Map((cpData || []).map(c => [c.id, c] as const));

      const { data: checkIns } = await supabase
        .from("check_ins")
        .select("id, checked_at, method, checkpoint_id, on_time, variance_minutes")
        .eq("officer_id", officerRecord.id)
        .gte("checked_at", today.toISOString())
        .lt("checked_at", tomorrow.toISOString());

      const checkInMap = new Map((checkIns || []).map(ci => [ci.checkpoint_id, ci] as const));

      const now = new Date();
      const items: CheckpointItem[] = schedules.map(s => {
        const cp = s.checkpoint_id ? cpMap.get(s.checkpoint_id) : null;
        const ci = s.checkpoint_id ? checkInMap.get(s.checkpoint_id) : null;
        const dueDate = new Date(s.due_at);
        const dueTime = `${String(dueDate.getHours()).padStart(2, "0")}:${String(dueDate.getMinutes()).padStart(2, "0")}`;

        let status: "done" | "due" | "pending" = "pending";
        if (ci || s.status === "completed") {
          status = "done";
        } else if (dueDate <= now) {
          status = "due";
        }

        return {
          id: s.checkpoint_id || s.id,
          scheduleId: s.id,
          name: cp?.name || "Checkpoint",
          zone: cp?.zone || "",
          due: dueTime,
          status,
          checkedAt: ci ? new Date(ci.checked_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : null,
          method: ci?.method || null,
          variance: ci ? (ci.on_time ? "On Time" : `+${ci.variance_minutes || 0}m`) : null,
          lat: cp?.lat ?? null,
          lng: cp?.lng ?? null,
          radius: cp?.radius ?? null,
        };
      });

      setCheckpoints(items);
    };
    fetchSchedule();
  }, [officerRecord?.id]);

  // Notifications: request permission & poll upcoming check-ins 
  const { requestPermission, notify, beep } = useNotifications();
  const notifiedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    // Ask for notification permission when officer opens dashboard
    requestPermission();
  }, [requestPermission]);

  useEffect(() => {
    if (!officerRecord?.id || checkpoints.length === 0) return;
    const check = () => {
      const now = Date.now();
      checkpoints.forEach((cp) => {
        if (cp.status === "done") return;
        const [h, m] = cp.due.split(":").map(Number);
        const dueDate = new Date();
        dueDate.setHours(h, m, 0, 0);
        const diff = dueDate.getTime() - now;
        // Notify 5 minutes before
        if (diff > 0 && diff <= 5 * 60 * 1000 && !notifiedRef.current.has(`pre-${cp.id}`)) {
          notifiedRef.current.add(`pre-${cp.id}`);
          beep();
          notify({
            title: "Check-in Due Soon",
            body: `${cp.name} (${cp.zone}) is due at ${cp.due}. Get ready!`,
            tag: `checkin-pre-${cp.id}`,
            requireInteraction: true,
            url: "/app/officer",
          });
        }
        // Notify when overdue
        if (diff < 0 && diff > -2 * 60 * 1000 && !notifiedRef.current.has(`overdue-${cp.id}`)) {
          notifiedRef.current.add(`overdue-${cp.id}`);
          beep();
          notify({
            title: "Check-in OVERDUE",
            body: `${cp.name} was due at ${cp.due}. Check in now!`,
            tag: `checkin-overdue-${cp.id}`,
            requireInteraction: true,
            url: "/app/officer",
          });
        }
      });
    };
    check();
    const interval = setInterval(check, 30_000); // check every 30s
    return () => clearInterval(interval);
  }, [officerRecord?.id, checkpoints, notify, beep]);

  // Listen for nudge notifications from security operator (late_reports with reason "Nudged")
  useEffect(() => {
    if (!officerRecord?.id) return;
    const channel = supabase
      .channel(`nudge-${officerRecord.id}`)
      .on("postgres_changes", {
        event: "INSERT",
        schema: "public",
        table: "late_reports",
        filter: `officer_id=eq.${officerRecord.id}`,
      }, (payload: any) => {
        const row = payload.new;
        if (row?.reason === "Nudged by security operator") {
          beep();
          notify({
            title: "Check-in Reminder!",
            body: `Your supervisor is requesting you check in at ${row.checkpoint || "your checkpoint"} now!`,
            tag: `nudge-${row.id}`,
            requireInteraction: true,
            url: "/app/officer",
          });
          toast.warning("Check-in Reminder from Supervisor", {
            description: `Please check in at ${row.checkpoint || "your assigned checkpoint"} immediately.`,
            duration: 15000,
          });
        }
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [officerRecord?.id, notify, beep]);

  const officerName = officerRecord?.name || profile?.full_name || "Officer";
  const officerFirstName = officerName.split(" ")[0].toUpperCase();
  const currentOfficerId = officerRecord?.id || "unknown";
  const currentSiteId = officerRecord?.site_id || "";

  const [sameSiteOfficers, setSameSiteOfficers] = useState<{ id: string; name: string; status: string; last_seen: string | null; site: string }[]>([]);

  // Fetch same-site officers from DB (not localStorage) to get real last_seen
  useEffect(() => {
    if (!currentSiteId || !currentOfficerId) return;
    const fetchSamesite = async () => {
      const { data } = await supabase
        .from("officers")
        .select("id, name, status, last_seen, site_id")
        .eq("site_id", currentSiteId)
        .neq("id", currentOfficerId);
      setSameSiteOfficers((data || []).map(o => ({ ...o, site: siteName })));
    };
    fetchSamesite();
    const interval = setInterval(fetchSamesite, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, [currentSiteId, currentOfficerId, siteName]);
  const [reportText, setReportText] = useState("");
  const [aiResult, setAiResult] = useState<null | { severity: string; category: string; action: string; escalate: boolean }>(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [incidentPhotos, setIncidentPhotos] = useState<File[]>([]);
  const [incidentPhotoPreview, setIncidentPhotoPreview] = useState<string[]>([]);
  const photoInputRef = useRef<HTMLInputElement>(null);

  const handlePhotoCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const newPhotos = [...incidentPhotos, ...files].slice(0, 5); // max 5 photos
    setIncidentPhotos(newPhotos);
    const previews = newPhotos.map(f => URL.createObjectURL(f));
    setIncidentPhotoPreview(prev => { prev.forEach(u => URL.revokeObjectURL(u)); return previews; });
  };

  const removePhoto = (idx: number) => {
    URL.revokeObjectURL(incidentPhotoPreview[idx]);
    setIncidentPhotos(prev => prev.filter((_, i) => i !== idx));
    setIncidentPhotoPreview(prev => prev.filter((_, i) => i !== idx));
  };

  // Shift password state
  const [passwordStep, setPasswordStep] = useState<"idle" | "verifying" | "verified" | "denied">("idle");
  const [shiftPassword, setShiftPassword] = useState<{ password: string; expiresAt: string } | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  // Face verification for shift password
  // Face verification for shift password
  const { startCamera, stopCamera, verifyFace, isVerifying, scanningState, verificationResult, error: faceError, setError: setFaceError, setVerificationResult } = useFaceVerification();

  const handleShowPassword = async () => {
    if (!officerRecord?.face_photo_url) {
      toast.error("No reference photo on file. Contact your supervisor to set up face verification.");
      setPasswordStep("denied");
      return;
    }
    setPasswordStep("verifying");
    try {
      const video = videoRef.current;
      if (!video) { setPasswordStep("denied"); return; }
      const cameraOk = await startCamera(video);
      if (!cameraOk) {
        toast.error(faceError || "Camera access denied");
        setPasswordStep("denied");
        return;
      }
      
      const result = await verifyFace(officerRecord.face_photo_url!, {
        officerId: officerRecord.id,
        officerName: officerRecord.name,
        siteId: officerRecord.site_id,
      });
      
      // Keep camera on for a moment to show success/error state in the circular UI
      setTimeout(() => stopCamera(), 2000);
      
      if (result.match && result.liveness && result.confidence >= 70) {
        // Face verified — fetch shift password via edge function
        const { data: pwdData } = await supabase.functions.invoke("get-shift-password", {
          body: { username: user?.user_metadata?.username || "" },
        });
        
        if (pwdData?.password) {
          setShiftPassword({ password: pwdData.password, expiresAt: pwdData.expiresAt });
          setPasswordStep("verified");
          toast.success(`Identity verified (${result.confidence}% confidence)`);
        } else {
          setPasswordStep("denied");
          toast.error("No active shift password found. Contact your supervisor.");
        }
      } else {
        // Wait for the user to see the error in the scanner before showing the denied screen
        setTimeout(() => setPasswordStep("denied"), 2000);
        if (!result.liveness) {
          toast.error("Liveness check failed: Spoofing attempt detected.");
        } else {
          toast.error(`Face verification failed: ${result.reason}`);
        }
      }
    } catch (err) {
      stopCamera();
      setPasswordStep("denied");
      toast.error("Face verification failed");
    }
  };
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [voiceBlob, setVoiceBlob] = useState<Blob | null>(null);
  const [isPlayingRecording, setIsPlayingRecording] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Map refs
  const mapInstanceRef = useRef<any>(null);
  const userLocationRef = useRef<{ lat: number; lng: number } | null>(null);
  const userMarkerRef = useRef<any>(null);
  const userCircleRef = useRef<any>(null);
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null);

  // Real-time clock
  const [currentTime, setCurrentTime] = useState(new Date());
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Old reminder code replaced by useNotifications hook above (lines 148-197)

  const doneCount = checkpoints.filter((c) => c.status === "done").length;
  const coverage = checkpoints.length > 0 ? Math.round((doneCount / checkpoints.length) * 100) : 0;

  // Live GPS tracking — high accuracy, push to DB
  const gpsIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (!navigator.geolocation) {
      // No hardcoded fallback — try to use checkpoint data from DB
      const cp = checkpoints.find(c => c.lat != null && c.lng != null);
      const fallback = cp ? { lat: cp.lat!, lng: cp.lng! } : { lat: 0, lng: 0 };
      userLocationRef.current = fallback;
      setUserLocation(fallback);
      return;
    }
    const watchId = navigator.geolocation.watchPosition(
      (pos) => {
        const loc = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        userLocationRef.current = loc;
        setUserLocation(loc);
        if (userMarkerRef.current) {
          userMarkerRef.current.setLatLng([loc.lat, loc.lng]);
          userMarkerRef.current.setPopupContent(
            `<div style="color:#000"><b>Your Position</b><br/><span style="font-family:monospace;font-size:11px">${loc.lat.toFixed(6)}, ${loc.lng.toFixed(6)}</span><br/><span style="font-size:10px;color:#666">Accuracy: Â±${Math.round(pos.coords.accuracy)}m</span></div>`
          );
        }
        if (userCircleRef.current) {
          userCircleRef.current.setLatLng([loc.lat, loc.lng]);
          userCircleRef.current.setRadius(Math.min(pos.coords.accuracy, 100));
        }
        // Pan map to keep user in view
        if (mapInstanceRef.current) {
          const bounds = mapInstanceRef.current.getBounds();
          if (!bounds.contains([loc.lat, loc.lng])) {
            mapInstanceRef.current.panTo([loc.lat, loc.lng]);
          }
        }
      },
      () => {
        const cp = checkpoints.find(c => c.lat != null && c.lng != null);
        const fallback = cp ? { lat: cp.lat!, lng: cp.lng! } : { lat: 0, lng: 0 };
        userLocationRef.current = fallback;
        setUserLocation(fallback);
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 }
    );

    // Push GPS + heartbeat to DB every 15 seconds
    const pushLocation = () => {
      const loc = userLocationRef.current;
      if (officerRecord?.id && loc) {
        supabase.from("officers").update({
          lat: loc.lat,
          lng: loc.lng,
          last_seen: new Date().toISOString(),
          status: "live",
        }).eq("id", officerRecord.id).then(() => {});
      }
    };
    pushLocation(); // immediate
    gpsIntervalRef.current = setInterval(pushLocation, 15000);

    // Set offline when browser tab is closed or navigated away
    const setOffline = () => {
      if (officerRecord?.id) {
        // Fire-and-forget update — works even during page unload
        supabase.from("officers").update({ status: "offline", last_seen: new Date().toISOString() }).eq("id", officerRecord.id);
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        setOffline();
      } else if (document.visibilityState === "visible") {
        pushLocation(); // come back online
      }
    };

    window.addEventListener("beforeunload", setOffline);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      navigator.geolocation.clearWatch(watchId);
      if (gpsIntervalRef.current) clearInterval(gpsIntervalRef.current);
      window.removeEventListener("beforeunload", setOffline);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [officerRecord?.id]);

  // Map initialization via callback ref
  const initMap = useCallback(async (container: HTMLDivElement) => {
    if (!container || mapInstanceRef.current) return;
    const L = await import("leaflet");
    if (!document.getElementById("leaflet-css")) {
      const link = document.createElement("link");
      link.id = "leaflet-css";
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
      document.head.appendChild(link);
      await new Promise(r => setTimeout(r, 300));
    }

    const loc = userLocationRef.current || { lat: 0, lng: 0 };
    const map = L.map(container).setView([loc.lat, loc.lng], 16);
    mapInstanceRef.current = map;

    L.tileLayer("https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png", {
      attribution: '&copy; CARTO',
      maxZoom: 19,
    }).addTo(map);
    addLayerToggle(L, map);

    // Your position
    const youIcon = L.divIcon({
      html: '<div style="width:22px;height:22px;border-radius:50%;background:#3B82F6;border:3px solid white;box-shadow:0 0 12px rgba(59,130,246,0.6);animation:pulse 2s infinite;"></div>',
      iconSize: [22, 22], iconAnchor: [11, 11], className: '',
    });
    const youMarker = L.marker([loc.lat, loc.lng], { icon: youIcon }).addTo(map)
      .bindPopup(`<div style="color:#000"><b>Your Position</b><br/><span style="font-family:monospace;font-size:11px">${loc.lat.toFixed(6)}, ${loc.lng.toFixed(6)}</span></div>`);
    userMarkerRef.current = youMarker;

    const accCircle = L.circle([loc.lat, loc.lng], {
      radius: 25, color: '#3B82F6', fillColor: 'rgba(59,130,246,0.08)', fillOpacity: 0.3, weight: 1, dashArray: '4 4',
    }).addTo(map);
    userCircleRef.current = accCircle;

    // Checkpoint markers — use REAL coordinates from DB
    checkpoints.forEach((cp) => {
      if (cp.lat == null || cp.lng == null) return;
      const pos: [number, number] = [cp.lat, cp.lng];
      const done = cp.status === "done";
      const due = cp.status === "due";
      const color = done ? '#06B6D4' : due ? '#EF4444' : '#8B5CF6';
      const icon = L.divIcon({
        html: `<div style="width:14px;height:14px;border-radius:50%;background:${color};border:2px solid white;box-shadow:0 0 6px ${color}40;"></div>`,
        iconSize: [14, 14], iconAnchor: [7, 7], className: '',
      });
      L.marker(pos, { icon }).addTo(map)
        .bindPopup(`<div style="color:#000"><b>${cp.name}</b><br/>${cp.zone}<br/>Due: ${cp.due}<br/>${done ? `<span style="color:#06B6D4">Done ${cp.checkedAt}</span>` : due ? '<span style="color:#EF4444">Due Now</span>' : 'Pending'}</div>`);
      
      // Geofence circle
      L.circle(pos, {
        radius: cp.radius || 25, color, fillColor: `${color}15`, fillOpacity: 0.2, weight: 1, dashArray: '5 5',
      }).addTo(map);
    });

    // Same-site officers from DB
    if (officerRecord?.site_id) {
      const { data: siteOfficers } = await supabase
        .from("officers")
        .select("id, name, lat, lng, status, last_seen, checkins")
        .eq("site_id", officerRecord.site_id)
        .neq("id", officerRecord.id);

      (siteOfficers || []).forEach((officer) => {
        if (officer.lat == null || officer.lng == null) return;
        const isLive = isOfficerLive(officer);
        const icon = L.divIcon({
          html: `<div style="width:${isLive ? 18 : 14}px;height:${isLive ? 18 : 14}px;border-radius:50%;background:${isLive ? '#10B981' : '#6B7280'};border:2px solid white;box-shadow:0 0 8px ${isLive ? 'rgba(16,185,129,0.5)' : 'rgba(107,114,128,0.3)'};${isLive ? '' : 'opacity:0.7;'}"></div>`,
          iconSize: [isLive ? 18 : 14, isLive ? 18 : 14], iconAnchor: [isLive ? 9 : 7, isLive ? 9 : 7], className: '',
        });
        L.marker([officer.lat, officer.lng], { icon }).addTo(map)
          .bindPopup(`<div style="color:#000"><b>${officer.name}</b>${isLive ? '' : ' (Offline)'}<br/>Check-ins: ${officer.checkins}<br/>Last seen: ${lastSeenAgo(officer.last_seen)}</div>`)
          .bindTooltip(officer.name, { permanent: true, direction: "top", offset: [0, -10], className: "officer-name-label" });
      });
    }

    // Patrol trail connecting completed checkpoints using real coordinates
    const trail = checkpoints
      .filter(cp => cp.status === "done" && cp.lat != null && cp.lng != null)
      .map(cp => [cp.lat!, cp.lng!] as [number, number]);
    if (trail.length > 1) {
      L.polyline(trail, { color: '#F0B429', dashArray: '8 6', weight: 2, opacity: 0.5 }).addTo(map);
    }

    // Auto-fit bounds to show all markers globally
    const allBounds: [number, number][] = [[loc.lat, loc.lng]];
    checkpoints.forEach(cp => { if (cp.lat != null && cp.lng != null) allBounds.push([cp.lat, cp.lng]); });
    if (allBounds.length > 1) {
      try { map.fitBounds(allBounds, { padding: [40, 40], maxZoom: 17 }); } catch {}
    }

    setTimeout(() => map.invalidateSize(), 200);
  }, [checkpoints, sameSiteOfficers, officerRecord?.id, officerRecord?.site_id]);

  // Cleanup map when leaving tab or when dependencies change so it re-initializes
  useEffect(() => {
    if (activeTab !== "map") {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        userMarkerRef.current = null;
        userCircleRef.current = null;
      }
    } else {
      // Force re-init when checkpoints change while on map tab
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
        userMarkerRef.current = null;
        userCircleRef.current = null;
      }
    }
  }, [activeTab, checkpoints]);

  const mapCallbackRef = useCallback((node: HTMLDivElement | null) => {
    if (node && activeTab === "map") {
      setTimeout(() => initMap(node), 100);
    }
  }, [activeTab, initMap]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setVoiceBlob(blob);
        stream.getTracks().forEach(t => t.stop());
      };
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => setRecordingTime(t => t + 1), 1000);
      toast.info("Recording started...");
    } catch {
      toast.error("Microphone access denied — type your report instead");
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (timerRef.current) clearInterval(timerRef.current);
      toast.success("Voice note recorded");
    }
  };

  const playRecording = () => {
    if (!voiceBlob) return;
    if (isPlayingRecording && audioRef.current) { audioRef.current.pause(); setIsPlayingRecording(false); return; }
    const url = URL.createObjectURL(voiceBlob);
    const audio = new Audio(url);
    audioRef.current = audio;
    audio.onended = () => setIsPlayingRecording(false);
    audio.play();
    setIsPlayingRecording(true);
  };

  const removeRecording = () => {
    setVoiceBlob(null);
    setRecordingTime(0);
    if (audioRef.current) audioRef.current.pause();
    setIsPlayingRecording(false);
  };

  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioRef.current) audioRef.current.pause();
    };
  }, []);

  const formatTime = (s: number) => `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;

  const handleReport = async () => {
    if (!reportText.trim() && !voiceBlob && incidentPhotos.length === 0) { toast.error("Please describe the incident, record a voice note, or attach a photo"); return; }
    setAnalyzing(true);
    try {
      // Upload photos if any
      let photoUrl: string | null = null;
      if (incidentPhotos.length > 0) {
        const file = incidentPhotos[0];
        const filePath = `${currentOfficerId}/${Date.now()}-${file.name}`;
        const { error: uploadErr } = await supabase.storage.from("incident-photos").upload(filePath, file, { contentType: file.type });
        if (!uploadErr) {
          const { data: urlData } = supabase.storage.from("incident-photos").getPublicUrl(filePath);
          photoUrl = urlData.publicUrl;
        }
      }

      // Insert incident into DB
      await supabase.from("incidents").insert({
        title: reportText.trim().slice(0, 80) || "Incident Report",
        description: reportText.trim(),
        officer_id: currentOfficerId !== "unknown" ? currentOfficerId : null,
        site_id: currentSiteId || null,
        severity: "medium",
        photo_url: photoUrl,
      });

      // Mock AI analysis (existing behavior)
      setAiResult({ severity: "Medium", category: "Suspicious Activity", action: "Increase patrol frequency in the area. Notify site operator. Document any vehicle registrations observed.", escalate: false });
      toast.success("Incident reported & analyzed by AI");
      setReportText("");
      setIncidentPhotos([]);
      setIncidentPhotoPreview(prev => { prev.forEach(u => URL.revokeObjectURL(u)); return []; });
    } catch (err) {
      toast.error("Failed to submit incident report");
    }
    setAnalyzing(false);
  };

  return (
    <div className="min-h-screen app-bg font-outfit flex flex-col">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-[hsl(var(--app-border-subtle))] app-surface-1 px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <button onClick={() => navigate("/")} className="p-1.5 rounded-lg text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-secondary))] hover:bg-[hsl(var(--app-surface-2))] transition-colors shrink-0" title="Back to website">
              <ArrowLeft className="w-4 h-4" />
            </button>
            <img src={secureflexLogo} alt="SecureFlex Logo" className="h-12 w-12 object-contain shrink-0" />
            <span className="font-bebas text-base sm:text-lg tracking-wider text-[hsl(var(--app-text-primary))]">SECUREFLEX</span>
          </div>
          <div className="hidden sm:block text-center">
            <p className="text-xs text-[hsl(var(--app-text-primary))] font-medium">{officerName}</p>
            <p className="text-[10px] text-[hsl(var(--app-text-muted))]">{siteName || "Unassigned"}</p>
            <p className="font-mono-data text-[10px] text-[hsl(var(--app-gold))]">{currentTime.toLocaleTimeString()} · {currentTime.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="flex items-center gap-1 px-2 py-1 rounded-full text-[10px] sm:text-xs text-[hsl(var(--app-green))] font-medium bg-[hsl(var(--app-green)/0.08)] border border-[hsl(var(--app-green)/0.2)] whitespace-nowrap">
              <span className="w-1.5 h-1.5 rounded-full bg-[hsl(var(--app-green))] animate-pulse-dot" />
              ON DUTY
            </span>
            <ThemeToggle />
            <button onClick={async () => {
              // Set offline BEFORE signing out (while auth session is still active for RLS)
              if (officerRecord?.id) {
                await supabase.from("officers").update({ status: "offline", last_seen: new Date().toISOString() }).eq("id", officerRecord.id);
              }
              await signOut();
              navigate("/app");
            }} className="p-2 rounded-lg text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-secondary))] hover:bg-[hsl(var(--app-surface-2))] transition-colors shrink-0">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <DashboardBreadcrumb role="Officer" activeTab={tabs.find(t => t.id === activeTab)?.label || activeTab} />

      {/* Content */}
      <main className="flex-1 pb-20 md:pb-6 md:pl-16 overflow-auto">
        <AnimatePresence mode="wait">
          <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="p-4 max-w-3xl mx-auto">

            {activeTab === "patrol" && (
              <div className="space-y-4">
                <div>
                  <h2 className="font-bebas text-2xl text-[hsl(var(--app-text-primary))]">GOOD {new Date().getHours() < 12 ? "MORNING" : new Date().getHours() < 18 ? "AFTERNOON" : "EVENING"}, {officerFirstName}</h2>
                  <p className="text-xs text-[hsl(var(--app-text-muted))]">{siteName || "Unassigned"}{officerRecord?.psira ? ` · ${officerRecord.psira}` : ""} · {new Date().toLocaleDateString()}</p>
                </div>

                <div className="grid grid-cols-3 gap-3">
                  <StatCard value={String(doneCount)} label="CHECK-INS" color="gold" />
                  <StatCard value={String(checkpoints.length - doneCount)} label="REMAINING" color="red" />
                  <StatCard value={`${coverage}%`} label="COVERAGE" color="green" />
                </div>

                {checkpoints.find((c) => c.status === "due") && (
                  <button onClick={() => handleCheckInAttempt(checkpoints.find((c) => c.status === "due")!)} className="w-full p-4 rounded-xl border border-[hsl(var(--app-red))] bg-[hsl(var(--app-red)/0.06)] text-left group hover:bg-[hsl(var(--app-red)/0.1)] transition-colors">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-[hsl(var(--app-red)/0.15)] flex items-center justify-center">
                        <Timer className="w-5 h-5 text-[hsl(var(--app-red))] animate-pulse" />
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-[hsl(var(--app-red))] uppercase tracking-wider">Check-in Due Now</p>
                        <p className="text-sm text-[hsl(var(--app-text-primary))] mt-0.5 font-medium">{checkpoints.find((c) => c.status === "due")?.name} · Due {checkpoints.find((c) => c.status === "due")?.due}</p>
                      </div>
                      <ChevronRight className="w-4 h-4 text-[hsl(var(--app-red)/0.5)] ml-auto group-hover:translate-x-0.5 transition-transform" />
                    </div>
                  </button>
                )}

                {checkpoints.map((cp) => (
                  <div key={cp.id} className={`app-card rounded-xl p-4 flex items-center gap-3 border-l-4 ${
                    cp.status === "done" ? "border-l-[hsl(var(--app-green))]" : cp.status === "due" ? "border-l-[hsl(var(--app-red))]" : "border-l-[hsl(var(--app-border-active))]"
                  } hover:-translate-y-0.5 transition-all cursor-pointer`}>
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                      cp.status === "done" ? "bg-[hsl(var(--app-green)/0.12)]" : cp.status === "due" ? "bg-[hsl(var(--app-red)/0.12)]" : "bg-[hsl(var(--app-surface-2))]"
                    }`}>
                      {cp.status === "done" ? <CheckCircle2 className="w-4 h-4 text-[hsl(var(--app-green))]" /> : 
                       cp.status === "due" ? <Radio className="w-4 h-4 text-[hsl(var(--app-red))] animate-pulse" /> :
                       <Circle className="w-4 h-4 text-[hsl(var(--app-text-muted))]" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-[hsl(var(--app-text-primary))] truncate">{cp.name}</p>
                      <p className="text-xs text-[hsl(var(--app-text-muted))]">{cp.zone} · {cp.status === "due" ? <span className="text-[hsl(var(--app-blue))]">Due {cp.due}</span> : `Due ${cp.due}`}</p>
                      {cp.status === "done" && (
                        <div className="flex items-center gap-1.5 mt-1">
                          <CheckCircle2 className="w-3 h-3 text-[hsl(var(--app-green))]" />
                          <span className="text-xs text-[hsl(var(--app-green))]">{cp.checkedAt}</span>
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-[hsl(var(--app-purple)/0.1)] text-[hsl(var(--app-purple))]">
                            <ScanFace className="w-3 h-3 inline mr-0.5" />{cp.method}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {cp.status !== "done" && (
                        <button onClick={() => handleCheckInAttempt(cp)} className="px-3 py-2 rounded-lg bg-[hsl(var(--app-gold)/0.08)] text-[hsl(var(--app-gold))] text-xs font-semibold border border-[hsl(var(--app-gold)/0.2)] hover:bg-[hsl(var(--app-gold)/0.15)] transition-colors flex items-center gap-1.5">
                          <Eye className="w-3.5 h-3.5" /> Check In
                        </button>
                      )}
                      <button className="p-2 rounded-lg text-[hsl(var(--app-red)/0.5)] hover:bg-[hsl(var(--app-red)/0.08)] hover:text-[hsl(var(--app-red))] transition-colors">
                        <AlertTriangle className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ===== MAP TAB ===== */}
            {activeTab === "map" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="font-bebas text-2xl text-[hsl(var(--app-text-primary))]">PATROL MAP</h2>
                    <p className="text-xs text-[hsl(var(--app-text-muted))]">Live GPS · {userLocation ? `${userLocation.lat.toFixed(4)}, ${userLocation.lng.toFixed(4)}` : 'Locating…'}</p>
                  </div>
                  <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-medium bg-[hsl(var(--app-green)/0.08)] text-[hsl(var(--app-green))] border border-[hsl(var(--app-green)/0.2)]">
                    <Crosshair className="w-3 h-3" /> TRACKING
                  </div>
                </div>
                
                <MapAddressSearch mapInstance={mapInstanceRef.current} />
                <div ref={mapCallbackRef} className="w-full h-[400px] rounded-xl border border-[hsl(var(--app-gold)/0.2)] overflow-hidden bg-[hsl(var(--app-surface-2))]" />
                
                <div className="flex flex-wrap gap-3 text-[10px] text-[hsl(var(--app-text-muted))]">
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: '#3B82F6' }} /> You</span>
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: '#06B6D4' }} /> Completed</span>
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: '#EF4444' }} /> Due Now</span>
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: '#8B5CF6' }} /> Pending</span>
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: '#10B981' }} /> Officer (Live)</span>
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full" style={{ background: '#6B7280' }} /> Officer (Offline)</span>
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded" style={{ background: '#F0B429', width: 10, height: 10 }} /> Patrol Trail</span>
                </div>

                {/* Same-site officers */}
                {sameSiteOfficers.length > 0 && (
                  <div className="space-y-2">
                    <p className="text-xs font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider">Officers on Your Site</p>
                    {sameSiteOfficers.map((officer) => {
                      const live = isOfficerLive(officer);
                      return (
                      <div key={officer.id} className="flex items-center gap-3 p-3 rounded-lg bg-[hsl(var(--app-surface-2)/0.5)]">
                        <span className={`w-2 h-2 rounded-full ${live ? "bg-[hsl(var(--app-green))] animate-pulse-dot" : "bg-gray-500"}`} />
                        <div className="flex-1">
                          <span className="text-xs text-[hsl(var(--app-text-primary))] font-medium">{officer.name}</span>
                          <span className="text-[10px] text-[hsl(var(--app-text-muted))] ml-2">{lastSeenAgo(officer.last_seen)}</span>
                        </div>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                          live ? "bg-[hsl(var(--app-green)/0.15)] text-[hsl(var(--app-green))]" : "bg-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))]"
                        }`}>{live ? "LIVE" : "OFFLINE"}</span>
                      </div>
                      );
                    })}
                  </div>
                )}

                {/* Checkpoint quick list below map */}
                <div className="space-y-2">
                  {checkpoints.map((cp) => (
                    <div key={cp.id} className="flex items-center gap-3 p-3 rounded-lg bg-[hsl(var(--app-surface-2)/0.5)]">
                      <span className={`w-2 h-2 rounded-full ${cp.status === "done" ? "bg-[hsl(var(--app-green))]" : cp.status === "due" ? "bg-[hsl(var(--app-red))]" : "bg-[hsl(var(--app-text-muted))]"}`} />
                      <span className="text-xs text-[hsl(var(--app-text-primary))] font-medium flex-1">{cp.name}</span>
                      <span className="text-[10px] font-mono-data text-[hsl(var(--app-text-muted))]">{cp.due}</span>
                      {cp.status === "done" && <CheckCircle2 className="w-3.5 h-3.5 text-[hsl(var(--app-green))]" />}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {activeTab === "schedule" && (
              <div className="space-y-4">
                <h2 className="font-bebas text-2xl text-[hsl(var(--app-text-primary))]">MY PATROL SCHEDULE</h2>
                <div className="app-card rounded-xl overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[hsl(var(--app-border-subtle))]">
                        {["Due Time", "Checkpoint", "Status", "Actual", "Variance", "Method"].map((h) => (
                          <th key={h} className="px-4 py-3 text-left text-[10px] font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {checkpoints.map((cp, i) => (
                        <tr key={cp.id} className={`border-b border-[hsl(var(--app-border-subtle))] ${i % 2 === 0 ? "bg-[hsl(var(--app-surface-2)/0.3)]" : ""}`}>
                          <td className="px-4 py-3 font-mono-data text-[hsl(var(--app-gold))] text-xs">{cp.due}</td>
                          <td className="px-4 py-3 text-[hsl(var(--app-text-primary))] text-xs font-medium">{cp.name}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium ${
                              cp.status === "done" ? "bg-[hsl(var(--app-green)/0.1)] text-[hsl(var(--app-green))]" :
                              cp.status === "due" ? "bg-[hsl(var(--app-blue)/0.1)] text-[hsl(var(--app-blue))] animate-pulse" :
                              "bg-[hsl(var(--app-border-subtle))] text-[hsl(var(--app-text-muted))]"
                            }`}>
                              {cp.status === "done" ? <><CheckCircle2 className="w-3 h-3" /> On Time</> : 
                               cp.status === "due" ? <><Radio className="w-3 h-3" /> Due Now</> : "Pending"}
                            </span>
                          </td>
                          <td className="px-4 py-3 font-mono-data text-[hsl(var(--app-text-secondary))] text-xs">{cp.checkedAt || "—"}</td>
                          <td className="px-4 py-3 font-mono-data text-xs">
                            {cp.variance ? <span className={cp.variance === "On Time" ? "text-[hsl(var(--app-green))]" : "text-[hsl(var(--app-gold))]"}>{cp.variance}</span> : "—"}
                          </td>
                          <td className="px-4 py-3">{cp.method ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] bg-[hsl(var(--app-purple)/0.1)] text-[hsl(var(--app-purple))]">
                              <ScanFace className="w-3 h-3" /> {cp.method}
                            </span>
                          ) : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === "log" && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="font-bebas text-2xl text-[hsl(var(--app-text-primary))]">MY CHECK-IN LOG</h2>
                  <span className="px-2.5 py-1 rounded-full text-xs bg-[hsl(var(--app-gold)/0.1)] text-[hsl(var(--app-gold))] font-medium border border-[hsl(var(--app-gold)/0.2)]">{doneCount} today</span>
                </div>
                {checkpoints.filter((c) => c.status === "done").map((cp) => (
                  <div key={cp.id} className="app-card rounded-xl p-4 space-y-2">
                    <div className="flex items-baseline gap-3">
                      <span className="font-mono-data text-[hsl(var(--app-gold))] text-sm font-semibold">{cp.checkedAt}</span>
                      <span className="text-sm font-semibold text-[hsl(var(--app-text-primary))]">{cp.name}</span>
                    </div>
                    <p className="text-xs text-[hsl(var(--app-text-muted))]">{cp.zone} · Riverside Office Park</p>
                    <div className="flex items-center gap-2 text-xs text-[hsl(var(--app-text-muted))]">
                      <ScanFace className="w-3 h-3 text-[hsl(var(--app-purple))]" />
                      <span>{cp.method}</span>
                      <span className="text-[hsl(var(--app-border-active))]">·</span>
                      <MapPin className="w-3 h-3" />
                      <span className="font-mono-data">-29.8838, 30.9462</span>
                    </div>
                    <p className="text-xs text-[hsl(var(--app-text-secondary))]">Scheduled {cp.due} · <span className="text-[hsl(var(--app-green))]">{cp.variance}</span></p>
                  </div>
                ))}
                {doneCount === 0 && <p className="text-center text-[hsl(var(--app-text-muted))] text-sm py-8">No check-ins recorded yet today</p>}
              </div>
            )}

            {/* ===== PASSWORD TAB ===== */}
            {activeTab === "password" && (
              <div className="space-y-4">
                <div>
                  <h2 className="font-bebas text-2xl text-[hsl(var(--app-text-primary))]">SHIFT PASSWORD</h2>
                  <p className="text-xs text-[hsl(var(--app-text-muted))]">Face verification required to view your shift password</p>
                </div>

                {passwordStep === "idle" && (
                  <div className="app-card rounded-xl p-6 text-center space-y-4">
                    <div className="w-20 h-20 rounded-full bg-[hsl(var(--app-gold)/0.08)] border-2 border-dashed border-[hsl(var(--app-gold)/0.3)] flex items-center justify-center mx-auto">
                      <Lock className="w-10 h-10 text-[hsl(var(--app-gold)/0.5)]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[hsl(var(--app-text-primary))]">Your shift password is locked</p>
                      <p className="text-xs text-[hsl(var(--app-text-muted))] mt-1">Verify your identity with face recognition to access</p>
                    </div>
                    <button
                      onClick={handleShowPassword}
                      className="w-full py-4 rounded-xl bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))] text-sm font-semibold hover:brightness-110 transition-all flex items-center justify-center gap-2"
                    >
                      <ScanFace className="w-5 h-5" /> Show My Shift Password
                    </button>
                  </div>
                )}

                {passwordStep === "verifying" && (
                  <div className="app-card rounded-xl p-6 text-center">
                    <FaceIDScanner 
                      videoRef={videoRef} 
                      state={scanningState} 
                      error={faceError} 
                    />
                    <button
                      onClick={() => { stopCamera(); setPasswordStep("idle"); }}
                      className="mt-4 px-4 py-2 text-xs text-[hsl(var(--app-text-muted))] hover:text-[hsl(var(--app-text-primary))] transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                )}

                {passwordStep === "verified" && shiftPassword && (
                  <div className="app-card rounded-xl p-6 text-center space-y-4">
                    <div className="w-16 h-16 rounded-full bg-[hsl(var(--app-green)/0.12)] flex items-center justify-center mx-auto">
                      <CheckCircle2 className="w-8 h-8 text-[hsl(var(--app-green))]" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-[hsl(var(--app-green))] uppercase tracking-wider">Identity Verified</p>
                      <p className="text-sm text-[hsl(var(--app-text-primary))] mt-1">Your shift password:</p>
                    </div>
                    <div className="p-5 rounded-xl bg-[hsl(var(--app-surface-2))] border border-[hsl(var(--app-gold)/0.3)]">
                      <p className="font-mono-data text-3xl font-bold text-[hsl(var(--app-gold))] tracking-[0.25em]">{shiftPassword.password}</p>
                    </div>
                    <p className="text-[10px] text-[hsl(var(--app-text-muted))]">
                      Expires: {new Date(shiftPassword.expiresAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} · {new Date(shiftPassword.expiresAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
                    </p>
                    <div className="flex gap-3">
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(shiftPassword.password);
                          toast.success("Password copied to clipboard");
                        }}
                        className="flex-1 py-3 rounded-xl border border-[hsl(var(--app-gold)/0.3)] text-sm font-medium text-[hsl(var(--app-gold))] hover:bg-[hsl(var(--app-gold)/0.1)] transition-colors flex items-center justify-center gap-1.5"
                      >
                        <Copy className="w-4 h-4" /> Copy Password
                      </button>
                      <button
                        onClick={() => { setPasswordStep("idle"); setShiftPassword(null); }}
                        className="flex-1 py-3 rounded-xl bg-[hsl(var(--app-surface-2))] text-sm font-medium text-[hsl(var(--app-text-secondary))] hover:bg-[hsl(var(--app-border-subtle))] transition-colors"
                      >
                        <Lock className="w-4 h-4 inline mr-1" /> Lock
                      </button>
                    </div>
                  </div>
                )}

                {passwordStep === "denied" && (
                  <div className="app-card rounded-xl p-6 text-center space-y-4">
                    <div className="w-16 h-16 rounded-full bg-[hsl(var(--app-red)/0.12)] flex items-center justify-center mx-auto">
                      <AlertTriangle className="w-8 h-8 text-[hsl(var(--app-red))]" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-[hsl(var(--app-text-primary))]">No Active Shift Password</p>
                      <p className="text-xs text-[hsl(var(--app-text-muted))] mt-1">Your shift password has not been issued yet or has expired. Contact your supervisor.</p>
                    </div>
                    <button
                      onClick={() => setPasswordStep("idle")}
                      className="w-full py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] text-sm text-[hsl(var(--app-text-muted))] hover:bg-[hsl(var(--app-surface-2))] transition-colors"
                    >
                      Try Again
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === "report" && (
              <div className="space-y-4">
                <h2 className="font-bebas text-2xl text-[hsl(var(--app-text-primary))]">REPORT INCIDENT</h2>
                <div className="app-card rounded-xl p-5 space-y-4">
                  <select className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] outline-none font-outfit focus:border-[hsl(var(--app-gold))] transition-colors">
                    <option>Select checkpoint...</option>
                    {checkpoints.map((cp) => <option key={cp.id}>{cp.name}</option>)}
                  </select>

                  <div className="relative">
                    <textarea
                      value={reportText}
                      onChange={(e) => setReportText(e.target.value.slice(0, 500))}
                      placeholder="Describe exactly what you observed — location, people involved, what happened, current situation…"
                      rows={5}
                      className="w-full px-4 py-3 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2))] text-sm text-[hsl(var(--app-text-primary))] placeholder:text-[hsl(var(--app-text-muted))] outline-none focus:border-[hsl(var(--app-gold))] resize-none font-outfit transition-colors"
                    />
                    <span className="absolute bottom-3 right-3 text-[10px] text-[hsl(var(--app-text-muted))] font-mono-data">{reportText.length} / 500</span>
                  </div>

                  {/* Voice Recording */}
                  <div className="p-4 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2)/0.5)]">
                    <div className="flex items-center gap-2 mb-3">
                      <Mic className="w-3.5 h-3.5 text-[hsl(var(--app-text-muted))]" />
                      <p className="text-xs font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider">Voice Note (Optional)</p>
                    </div>
                    
                    {!voiceBlob && !isRecording && (
                      <button onClick={startRecording} className="w-full py-3.5 rounded-xl border border-dashed border-[hsl(var(--app-gold)/0.3)] text-sm text-[hsl(var(--app-gold))] hover:bg-[hsl(var(--app-gold)/0.05)] transition-colors flex items-center justify-center gap-2">
                        <Mic className="w-4 h-4" /> Tap to Record Voice Note
                      </button>
                    )}

                    {isRecording && (
                      <div className="flex items-center gap-4">
                        <button onClick={stopRecording} className="w-12 h-12 rounded-full bg-[hsl(var(--app-red))] text-white flex items-center justify-center shrink-0">
                          <Square className="w-4 h-4" />
                        </button>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-[hsl(var(--app-red))] animate-pulse" />
                            <span className="text-sm text-[hsl(var(--app-red))] font-medium">Recording…</span>
                            <span className="text-sm font-mono-data text-[hsl(var(--app-text-secondary))]">{formatTime(recordingTime)}</span>
                          </div>
                          <div className="h-1 rounded-full bg-[hsl(var(--app-border-subtle))] mt-2 overflow-hidden">
                            <motion.div className="h-full bg-[hsl(var(--app-red))]" animate={{ width: ["20%", "80%", "40%", "90%", "30%"] }} transition={{ duration: 1.5, repeat: Infinity }} />
                          </div>
                        </div>
                      </div>
                    )}

                    {voiceBlob && !isRecording && (
                      <div className="flex items-center gap-3">
                        <button onClick={playRecording} className={`w-10 h-10 rounded-full flex items-center justify-center transition-all shrink-0 ${
                          isPlayingRecording ? "bg-[hsl(var(--app-gold))] text-[hsl(var(--app-bg))]" : "bg-[hsl(var(--app-gold)/0.1)] text-[hsl(var(--app-gold))]"
                        }`}>
                          {isPlayingRecording ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                        </button>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <Mic className="w-3 h-3 text-[hsl(var(--app-green))]" />
                            <span className="text-xs font-medium text-[hsl(var(--app-green))]">Voice note recorded</span>
                            <span className="text-[10px] font-mono-data text-[hsl(var(--app-text-muted))]">{formatTime(recordingTime)}</span>
                          </div>
                          <div className="h-1 rounded-full bg-[hsl(var(--app-border-subtle))] mt-1.5">
                            <div className="h-full rounded-full bg-[hsl(var(--app-green))] w-full" />
                          </div>
                        </div>
                        <button onClick={removeRecording} className="p-2 rounded-lg text-[hsl(var(--app-red)/0.5)] hover:bg-[hsl(var(--app-red)/0.08)] hover:text-[hsl(var(--app-red))] transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Photo Capture */}
                  <div className="p-4 rounded-xl border border-[hsl(var(--app-border-subtle))] bg-[hsl(var(--app-surface-2)/0.5)]">
                    <div className="flex items-center gap-2 mb-3">
                      <Camera className="w-3.5 h-3.5 text-[hsl(var(--app-text-muted))]" />
                      <p className="text-xs font-semibold text-[hsl(var(--app-text-muted))] uppercase tracking-wider">Photos (Optional · max 5)</p>
                    </div>

                    <input
                      ref={photoInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      multiple
                      onChange={handlePhotoCapture}
                      className="hidden"
                    />

                    {incidentPhotoPreview.length > 0 && (
                      <div className="flex gap-2 flex-wrap mb-3">
                        {incidentPhotoPreview.map((url, idx) => (
                          <div key={idx} className="relative w-20 h-20 rounded-lg overflow-hidden border border-[hsl(var(--app-border-subtle))]">
                            <img src={url} alt={`Incident photo ${idx + 1}`} className="w-full h-full object-cover" />
                            <button
                              onClick={() => removePhoto(idx)}
                              className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-[hsl(var(--app-bg)/0.8)] text-[hsl(var(--app-red))] flex items-center justify-center"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}

                    {incidentPhotos.length < 5 && (
                      <button
                        onClick={() => photoInputRef.current?.click()}
                        className="w-full py-3.5 rounded-xl border border-dashed border-[hsl(var(--app-gold)/0.3)] text-sm text-[hsl(var(--app-gold))] hover:bg-[hsl(var(--app-gold)/0.05)] transition-colors flex items-center justify-center gap-2"
                      >
                        <Camera className="w-4 h-4" /> Take or Attach Photo
                      </button>
                    )}
                  </div>

                  <button onClick={handleReport} disabled={analyzing} className="w-full py-4 rounded-xl bg-[hsl(var(--app-red))] text-white font-semibold hover:brightness-110 transition-all flex items-center justify-center gap-2 disabled:opacity-50">
                    {analyzing ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <><Bot className="w-4 h-4" /> Submit & AI Analyse</>}
                  </button>
                </div>

                {aiResult && (
                  <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="app-card rounded-xl p-5 space-y-3">
                    <div className="flex flex-wrap gap-2">
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[hsl(var(--app-gold)/0.1)] text-[hsl(var(--app-gold))]">{aiResult.severity}</span>
                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[hsl(var(--app-blue)/0.1)] text-[hsl(var(--app-blue))]">{aiResult.category}</span>
                    </div>
                    <p className="text-sm text-[hsl(var(--app-text-secondary))] leading-relaxed">{aiResult.action}</p>
                    {aiResult.escalate && (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold bg-[hsl(var(--app-red)/0.1)] text-[hsl(var(--app-red))] animate-pulse">
                        <AlertTriangle className="w-3 h-3" /> ESCALATE
                      </span>
                    )}
                    <button className="w-full py-3 rounded-xl border border-[hsl(var(--app-green)/0.3)] text-[hsl(var(--app-green))] text-sm font-semibold hover:bg-[hsl(var(--app-green)/0.05)] transition-colors flex items-center justify-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4" /> Acknowledged
                    </button>
                  </motion.div>
                )}
              </div>
            )}

            {activeTab === "shift" && officerRecord && (
              <ShiftClockSection
                officerRecord={officerRecord}
                siteName={siteName}
                onClockedInChange={setIsClockedIn}
              />
            )}

            {activeTab === "profile" && <ProfileSection />}

          </motion.div>
        </AnimatePresence>
      </main>

      {/* Bottom nav (mobile) */}
      <nav className="fixed bottom-0 left-0 right-0 md:hidden border-t border-[hsl(var(--app-border-subtle))] app-surface-1 flex z-40 backdrop-blur-sm">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 py-3 flex flex-col items-center gap-1 text-[10px] font-medium transition-colors ${
                activeTab === tab.id ? "text-[hsl(var(--app-gold))] border-t-2 border-[hsl(var(--app-gold))]" : "text-[hsl(var(--app-text-muted))]"
              }`}
            >
              <Icon className="w-4.5 h-4.5" />
              {tab.label}
            </button>
          );
        })}
      </nav>

      {/* Desktop side tabs */}
      <nav className="hidden md:flex fixed left-0 top-[57px] bottom-0 w-16 flex-col items-center gap-1.5 pt-4 border-r border-[hsl(var(--app-border-subtle))] app-surface-1">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center gap-0.5 text-[9px] font-medium transition-all ${
                activeTab === tab.id ? "bg-[hsl(var(--app-gold)/0.08)] text-[hsl(var(--app-gold))] border border-[hsl(var(--app-gold)/0.2)]" : "text-[hsl(var(--app-text-muted))] hover:bg-[hsl(var(--app-surface-2))]"
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </nav>

      {checkInTarget && (
        <CheckInModal
          checkpoint={{ ...checkInTarget, site_id: officerRecord?.site_id }}
          onClose={() => setCheckInTarget(null)}
          officerFacePhotoUrl={officerRecord?.face_photo_url}
          officerInfo={officerRecord ? { officerId: officerRecord.id, officerName: officerRecord.name, siteId: officerRecord.site_id } : undefined}
        />
      )}
    </div>
  );
};

const StatCard = ({ value, label, color }: { value: string; label: string; color: "gold" | "red" | "green" }) => {
  const colorMap = { gold: "app-gold", red: "app-red", green: "app-green" };
  return (
    <div className="app-card rounded-xl p-4 text-center">
      <p className={`font-bebas text-3xl text-[hsl(var(--${colorMap[color]}))]`}>{value}</p>
      <p className="text-[10px] font-semibold tracking-wider text-[hsl(var(--app-text-muted))] uppercase mt-1">{label}</p>
    </div>
  );
};

export default OfficerDashboard;
